import { TextField, Typography, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { maybeTransformEmptyRichTextContentToNull } from 'shared/utils';

type CriticalAlertConfirmationProps = {
  onConfirmationChange: (confirmed: boolean) => void;
  messageFieldName?: string;
};

export const COUNTDOWN_SECONDS = 10;

export const CriticalAlertConfirmation = ({
  onConfirmationChange,
  messageFieldName = 'messageText',
}: CriticalAlertConfirmationProps): JSX.Element | null => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const message = useWatch({ name: messageFieldName });

  const confirmationPhrase = t(
    'addIncidentReport.criticalConfirmation.confirmationPhrase',
  );
  const isConfirmed = inputValue === confirmationPhrase;

  const clearCountdownInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isConfirmed) {
      setCountdown(COUNTDOWN_SECONDS);
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearCountdownInterval();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearCountdownInterval();
      setCountdown(COUNTDOWN_SECONDS);
      onConfirmationChange(false);
    }

    return clearCountdownInterval;
  }, [isConfirmed, clearCountdownInterval, onConfirmationChange]);

  useEffect(() => {
    if (countdown === 0) {
      onConfirmationChange(true);
    }
  }, [countdown, onConfirmationChange]);

  // This may be a rich text field
  if (!maybeTransformEmptyRichTextContentToNull(message ?? '')) return null;

  const adornment = isConfirmed ? <CheckCircleIcon color="success" /> : null;

  return (
    <>
      <Typography variant="h3" sx={{ fontWeight: 'fontWeightBold', mb: 2 }}>
        {t('addIncidentReport.criticalConfirmation.heading')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        <Trans i18nKey="addIncidentReport.criticalConfirmation.description">
          Sending <b>Critical Alerts</b> requires confirmation from two other
          senior staff reporters.
        </Trans>
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={2}>
        <Typography variant="body1">
          {t('addIncidentReport.criticalConfirmation.confirmationInstructions')}
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontWeight: 'bold', userSelect: 'none' }}
        >
          {confirmationPhrase}
        </Typography>
      </Stack>
      <TextField
        fullWidth
        label={t('addIncidentReport.criticalConfirmation.inputLabel')}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        required
        slotProps={{ input: { endAdornment: adornment } }}
      />
      {isConfirmed && (
        <Typography
          variant="body2"
          sx={{ fontWeight: 'fontWeightBold', mt: 2 }}
        >
          {t('addIncidentReport.criticalConfirmation.countdown', {
            remaining: countdown,
          })}
        </Typography>
      )}
    </>
  );
};
